import React from 'react';
import { Button, Progress } from 'reactstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    DateFilterDefaults,
    IconCellRenderer,
    indexCellRenderer,
    LinkCellRenderer,
    TextFilterDefaults,
} from '../../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../../common/dataGrid/DataGridToolbar';
import CommonContext, { ApiRoutes, AppNavPaths } from '../../Common';
import {
    PageHeading, PageWrap, Textarea, toasty,
} from '../../common/forms/FormElements';
import authService from '../../api-authorization/AuthorizeService';
import { Can } from '../../Can';
import { QuickJob } from '../../NavMenu/QuickJob';
import { Modal } from '../../common/Modal';
import OrganizationContactForm from '../../organization/OrganizationContactForm';
import { JobRequestSlideout } from './JobRequestSlideout';
import { JobRequestXlsxImportSlideout } from './JobRequestXlsxImportSlideout';
import { JobRequestStatuses } from './JobRequest';

const handleSaveError = (error) => toasty.error(`Unable to save contact: ${error.toString()}`);

class JobRequestIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        const params = new URLSearchParams(props.location.search);

        this.state = {
            loading: true,
            rowData: [],
            quickJobModalOpen: false,
            selectedJobRequest: null,
            jobRequestStatuses: [],
            selectedRow: null,
            contactFormIsReadOnly: false,
            showContactsForm: false,
            selectedContact: null,
            companies: [],
            jobRequestDetailsOpen: params.has('r'),
            jobRequestId: params.has('r') ? params.get('r') : null,
            xlsxImportOpen: false,
        };

        this.onEditContact = this.onEditContact.bind(this);
        this.contactFormRef = React.createRef();
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentWillUnmount() {
        if (this._subscription) {
            authService.unsubscribe(this._subscription);
        }
    }

    onCreateJob = (data) => {
        this.setState({
            quickJobModalOpen: true,
            selectedJobRequest: data,
        });
    };

    openXlsxImport = () => this.setState({ xlsxImportOpen: true });

    closeXlsxImport = () => this.setState({ xlsxImportOpen: false });

    onXlsxImported = () => {
        this.setState({ xlsxImportOpen: false });
        this.state.gridOptions?.refresh();
    };

    onEditContact = async (id) => {
        const selectedContact = await httpClient.js(
            ApiRoutes.locationContact.byId(id),
        );
        this.context.setFormOpened(true);
        this.setState({ selectedContact, showContactsForm: true });
    };

    onOrganizationContactSaved = async () => {
        this.setState({ contactFormIsReadOnly: true });

        const { gridOptions } = this.state;

        const saved = await this.updateLocationContact();

        if (saved) {
            this.contactFormRef.current.resetForm();

            gridOptions.refresh();

            this.setState({
                showContactsForm: false,
            });
        }

        this.setState({ contactFormIsReadOnly: false });
    };

    updateLocationContact = async () => {
        const { selectedContact } = this.state;

        const updateResult = await httpClient
            .put(
                ApiRoutes.locationContact.update(selectedContact.id),
                selectedContact,
                httpClient.format.none,
            )
            .catch(handleSaveError);

        return Boolean(updateResult.ok);
    };

    onRejectButtonClick = (data) => {
        Modal.show({
            title: 'Reject Job Request',
            SaveButtonProps: {
                color: 'danger',
                label: 'Yes, reject this request',
            },
            onSubmit: async ({ notes }) => {
                try {
                    await httpClient.put(
                        ApiRoutes.jobRequest.reject(data.id),
                        { notes },
                    );

                    toasty.success('Job Request', 'This job request has been rejected');

                    if (this.state.gridApi) {
                        this.state.gridApi.setDatasource(this.state.dataSource);
                    }
                } catch {
                    toasty.error('Job Request', 'Failed to update status.');
                }
            },
            body: () => (
                <Textarea
                    label="Please enter a rejection reason"
                    name="notes"
                    maxLength={500}
                    required
                    error="A reason for this rejection is required"
                />
            ),
        });
    };

    onApproveButtonClick = async (data) => {
        try {
            await httpClient.put(
                ApiRoutes.jobRequest.updateStatus(data.id, JobRequestStatuses.Approved),
            );

            toasty.success('Job Request', 'This job request has been approved');

            if (this.state.gridApi) {
                this.state.gridApi.setDatasource(this.state.dataSource);
            }
        } catch {
            toasty.error('Job Request', 'Failed to update status.');
        }
    };

    onRowClicked = (event) => {
        const selection = event.api.getSelectedRows();
        const row = selection.length ? selection[0] : null;

        if (row) {
            this.setState({ selectedRow: row });
        }
    };

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (isAuthenticated) {
            const gridOptions = createGridOptions(this);

            const [jobRequestStatuses, usStates, allCompanies] = await Promise.all([
                httpClient.js(ApiRoutes.jobRequests.getStatuses()),
                httpClient.js(ApiRoutes.typeAheads.USStates()),
                httpClient.js(ApiRoutes.company.all()),
            ]);

            const companies = allCompanies.filter((c) => Boolean(c.active && c.locationId));

            gridOptions.onRowClicked = this.onRowClicked;

            gridOptions.components = {
                selectFilter: DataGridSelectFilter,
                iconRenderer: IconCellRenderer,
                linkCellRenderer: LinkCellRenderer,
                selectFloatingFilter: DataGridSelectFloatingFilter,
            };

            const stateFilterParams = {
                suppressFilterButton: true,
                options: usStates,
                optionsLabel: 'label',
                optionsValue: 'value',
            };

            const statusFilterParams = {
                suppressFilterButton: true,
                options: jobRequestStatuses,
                optionsLabel: 'label',
                optionsValue: 'value',
            };

            gridOptions.columnDefs = [
                {
                    flex: 0,
                    maxWidth: 35,
                    headerName: '',
                    valueGetter: 'node.id',
                    sortable: false,
                    cellRenderer: indexCellRenderer,
                },
                {
                    colId: 'CreatedOn',
                    headerName: 'Date Submitted',
                    field: 'createdOn',
                    sortable: true,
                    sort: 'desc',
                    minWidth: 120,
                    sort: { direction: 'desc', priority: 0 },
                    filter: 'agDateColumnFilter',
                    filterParams: DateFilterDefaults,
                    valueFormatter: (params) => params.value ? moment(params.value).format('YYYY-MM-DD') : '',
                },
                {
                    colId: 'CustomerCompany.CompanyName',
                    flex: 2,
                    headerName: 'Customer',
                    field: 'customerName',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'Contact',
                    headerName: 'Requested By',
                    field: 'contact',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: 'linkCellRenderer',
                    cellRendererParams: {
                        clicked: this.onEditContact,
                        nameField: 'contact',
                        idField: 'locationContactId',
                        title: 'Edit this Contact',
                    },
                },
                {
                    colId: 'JobDescription',
                    flex: 2,
                    headerName: 'Description',
                    field: 'description',
                    tooltipField: 'description',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'Notes',
                    flex: 2,
                    headerName: 'Notes',
                    field: 'notes',
                    tooltipField: 'notes',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'WorkOrderNumber',
                    flex: 2,
                    headerName: 'WO #',
                    field: 'workOrderNumber',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'PurchaseOrderNumber',
                    flex: 2,
                    headerName: 'PO #',
                    field: 'purchaseOrderNumber',
                    sortable: true,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'Location.City',
                    sortable: false,
                    sort: { direction: 'asc' },
                    headerName: 'City',
                    field: 'city',
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'Location.State',
                    sortable: false,
                    sort: { direction: 'asc' },
                    headerName: 'State',
                    field: 'state',
                    flex: 0,
                    width: 140,
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: stateFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: stateFilterParams,
                },
                {
                    colId: 'RequestedStartDate',
                    headerName: 'Requested Date',
                    field: 'requestedStartDate',
                    sortable: true,
                    minWidth: 120,
                    sort: { direction: 'desc', priority: 0 },
                    filter: 'agDateColumnFilter',
                    filterParams: DateFilterDefaults,
                    valueFormatter: (params) => params.value ? moment(params.value).format('YYYY-MM-DD') : '',
                },
                {
                    colId: 'JobRequestStatusId',
                    headerName: 'Status',
                    field: 'jobRequestStatusName',
                    sortable: true,
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: statusFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: statusFilterParams,
                    cellRenderer: (params) => {
                        if (!params.data) return null;

                        const {
                            projectId, jobId, jobRequestStatusName, jobRequestStatusId,
                        } = params.data;

                        if (jobRequestStatusId === JobRequestStatuses.JobCreated) {
                            const to = projectId
                                ? `${AppNavPaths.Project}/${projectId}?openJob=${jobId}`
                                : `${AppNavPaths.Job}/${jobId}`;

                            return (
                                <Link
                                    className="site-link"
                                    to={to}
                                    target="_blank"
                                >
                                    {jobRequestStatusName}
                                </Link>
                            );
                        }

                        return jobRequestStatusName;
                    },
                },
            ];

            const dataSource = createDataSource(
                ApiRoutes.jobRequests.search(),
                gridOptions,
            );

            this.setState({
                loading: false,
                gridOptions,
                dataSource,
                jobRequestStatuses,
                companies,
            });
        }
    }

    render() {
        if (this.state.loading) return <Progress />;

        const {
            rowData, gridOptions, selectedRow, contactFormIsReadOnly,
            showContactsForm, selectedContact, companies,
        } = this.state;

        return (
            <>
                <PageWrap>
                    <PageHeading className="d-flex justify-content-between align-items-center">
                        <div>
                            <FontAwesomeIcon
                                icon={faClipboardList}
                                className="mr-2 text-muted"
                            />
                            <span>Job Requests</span>
                        </div>

                        <div>
                            <Can
                                I="edit"
                                a="job_request"
                            >
                                <Button
                                    size="sm"
                                    color="success"
                                    className="mr-2"
                                    onClick={this.openXlsxImport}
                                >
                                    <FontAwesomeIcon
                                        icon={faFileExcel}
                                        className="mr-1"
                                    />
                                    Import
                                </Button>

                                <Button
                                    size="sm"
                                    color="primary"
                                    className="mr-2"
                                    onClick={() => this.onApproveButtonClick(selectedRow)}
                                    disabled={
                                        selectedRow?.jobRequestStatusId !== JobRequestStatuses.Submitted
                                        && selectedRow?.jobRequestStatusId !== JobRequestStatuses.Rejected
                                    }
                                >
                                    Approve Request
                                </Button>

                                <Button
                                    size="sm"
                                    color="outline-danger"
                                    className="mr-2"
                                    onClick={() => this.onRejectButtonClick(selectedRow)}
                                    disabled={
                                        selectedRow?.jobRequestStatusId !== JobRequestStatuses.Submitted
                                        && selectedRow?.jobRequestStatusId !== JobRequestStatuses.Approved
                                    }
                                >
                                    Reject Request
                                </Button>
                            </Can>

                            <Can
                                I="create"
                                a="job"
                            >
                                <Button
                                    size="sm"
                                    color="success"
                                    onClick={() => this.onCreateJob(selectedRow)}
                                    disabled={selectedRow?.jobRequestStatusId !== JobRequestStatuses.Approved}
                                >
                                    Create Job
                                </Button>
                            </Can>
                        </div>
                    </PageHeading>

                    <DataGridToolbar
                        entity="jobrequest"
                        hideExcelButton
                        hideAdd
                        gridOptions={this.state.gridOptions}
                        gridApi={this.state.gridApi}
                        dataSource={this.state.dataSource}
                    />

                    <DataGrid
                        domLayout="normal"
                        rowData={rowData}
                        gridOptions={gridOptions}
                        gridStatus={this.state.gridStatus}
                    />
                </PageWrap>

                <JobRequestXlsxImportSlideout
                    show={this.state.xlsxImportOpen}
                    onClose={this.closeXlsxImport}
                    onImported={this.onXlsxImported}
                />

                <OrganizationContactForm
                    ref={this.contactFormRef}
                    readOnly={contactFormIsReadOnly}
                    allowContactTypeEdit={!contactFormIsReadOnly}
                    allowAllContactTypes
                    show={showContactsForm}
                    contact={selectedContact}
                    companies={companies}
                    showCompany
                    onClose={() => {
                        this.setState({ showContactsForm: false });
                    }}
                    onChange={(e) => {
                        const { selectedContact } = this.state;
                        selectedContact[e.target.name] = e.target.value;
                        this.setState({ selectedContact });
                    }}
                    onCheckedChanged={(e) => {
                        const { selectedContact } = this.state;
                        selectedContact[e.target.name] = e.target.checked;
                        this.setState({ selectedContact });
                    }}
                    onContactTypeChanged={(items) => {
                        const { selectedContact } = this.state;
                        selectedContact.contactTypes = (items || []).map((x) => x.value);

                        this.setState({ selectedContact });
                    }}
                    onCompanyChanged={(item) => {
                        const { selectedContact } = this.state;
                        const id = (item ?? {}).value ?? '';
                        selectedContact.companyId = id;
                        this.setState({ selectedContact });
                    }}
                    onSaveCallback={this.onOrganizationContactSaved}
                />

                <QuickJob
                    show={this.state.quickJobModalOpen}
                    onClose={() => this.setState({ quickJobModalOpen: false, selectedJobRequest: null })}
                    selectedJobRequest={this.state.selectedJobRequest}
                />

                <JobRequestSlideout
                    show={this.state.jobRequestDetailsOpen}
                    jobRequestId={this.state.jobRequestId}
                    onClose={() => this.setState({ jobRequestDetailsOpen: false, jobRequestId: null })}
                />
            </>
        );
    }
}

export default JobRequestIndex;